import { apiClient } from 'src/lib/api-client';

// Types for subscription service
export interface SubscriptionStatus {
  isPro: boolean;
  proEndDateUtc: string | null;
  freeTrialCount: number;
  isActive: boolean;
}

export interface FeatureAccessResponse {
  errorCode?: string;
  message?: string;
  isSuccess: boolean;
  data?: {
    freeTrialCount?: number;
    requiresConfirmation?: boolean;
  };
}

export interface SubscriptionPlan {
  id: string;
  name: string;
  description: string;
  price: number;
  currency: string;
  interval: string;
  intervalCount: number;
  isActive: boolean;
  features: string | string[];
}

export interface SubscriptionPlansResponse {
  items: SubscriptionPlan[];
  totalCount: number;
  isSuccess: boolean;
  message?: string;
}

export interface ActivateSubscriptionRequest {
  months?: number;
}

export interface CreateSubscriptionRequest {
  planId: string;
}

export interface CreateSubscriptionResponse {
  isSuccess: boolean;
  message?: string;
  data?: {
    clientSecret: string;
    subscriptionId?: string;
    setupIntentId?: string;
    requiresPaymentMethod?: boolean;
    requiresAction?: boolean;
    checkoutUrl?: string;
  };
}

export interface SubscriptionHistoryResponse {
  subscriptionHistory: {
    id: string;
    amount: number;
    status: string;
    transactionDateOnUtc: string;
    description: string;
  }[];
  userSubscription?: {
    userId: string;
    subscriptionPlanId: string;
    name: string;
    price: number;
    interval: string;
    endDateOnUtc: string;
  };
  isSuccess: boolean;
  message?: string;
}

// Interface matching UserPaymentInvoicesEntity structure
// Ideally, this would be in a shared types file (e.g., src/types/subscription.ts)
export interface PaymentInvoiceItem {
  id: string; // BaseEntity Id
  stripeInvoiceId: string;
  subscriptionId?: string;
  stripePriceIdUsed?: string;
  subscriptionPlanDbId?: string;
  // subscriptionPlan?: { name: string }; // Optional: Add if backend includes it
  invoicePdf?: string;
  hostedInvoiceUrl?: string;
  amount: number;
  currency?: string;
  periodStartDate: string; // ISO date string
  periodEndDate: string; // ISO date string
  status: string;
  paymentDateUtc: string; // ISO date string
}

// Interface for the response from the /invoices endpoint
export interface PaymentInvoicesResponse {
  items: PaymentInvoiceItem[];
  isSuccess: boolean;
  message?: string;
  errorCode?: string;
  // Add other potential response fields if needed (e.g., totalCount)
}

const NO_SUBSCRIPTION: SubscriptionStatus = {
  isPro: false,
  proEndDateUtc: null,
  freeTrialCount: 0,
  isActive: false,
};

// Create the subscription service
export const subscriptionService = {
  // Get current subscription status
  async getStatus(): Promise<SubscriptionStatus> {
    try {
      const response = await apiClient.get<{ data?: SubscriptionStatus }>(
        '/subscriptions/status'
      );
      return response.data ?? NO_SUBSCRIPTION;
    } catch (error) {
      console.error('Error fetching subscription status:', error);
      return NO_SUBSCRIPTION;
    }
  },

  // Get subscription plans
  async getPlans(): Promise<SubscriptionPlansResponse> {
    try {
      return await apiClient.get<SubscriptionPlansResponse>('/subscriptions/plans');
    } catch (error) {
      console.error('Error fetching subscription plans:', error);
      return {
        items: [],
        totalCount: 0,
        isSuccess: false,
        message: 'Failed to load subscription plans',
      };
    }
  },

  // Get subscription history
  async getSubscriptionHistory(): Promise<SubscriptionHistoryResponse> {
    try {
      const response = await apiClient.get<any>('/subscriptions/payments');
      return {
        subscriptionHistory: response.data?.subscriptionHistory || [],
        userSubscription: response.data?.userSubscription,
        isSuccess: !response.errorCode,
        message: response.message,
      };
    } catch (error) {
      console.error('Error fetching subscription history:', error);
      return {
        subscriptionHistory: [],
        isSuccess: false,
        message: 'Failed to load subscription history',
      };
    }
  },

  // Get user payment invoices
  async getPaymentInvoices(): Promise<PaymentInvoicesResponse> {
    try {
      // Assuming the backend endpoint is /api/subscription/invoices
      const response =
        await apiClient.get<PaymentInvoicesResponse>('/subscriptions/invoices');
      // Ensure items array exists, even if null/undefined in response
      return { ...response, items: response.items || [] };
    } catch (error) {
      console.error('Error fetching payment invoices:', error);
      return {
        items: [],
        isSuccess: false,
        message: 'Failed to load payment history',
        errorCode: 'NETWORK_ERROR',
      };
    }
  },

  // Check if a feature can be accessed
  async checkFeatureAccess(featureName: string): Promise<FeatureAccessResponse> {
    try {
      // "No access" is an answer, not a failure: callers read it as an error code.
      const response = await apiClient.get<{ message: string; data: { hasAccess: boolean } }>(
        `/subscriptions/features/${featureName}`
      );
      const hasAccess = Boolean(response.data?.hasAccess);
      return {
        isSuccess: hasAccess,
        message: response.message,
        errorCode: hasAccess ? undefined : 'FEATURE_NOT_AVAILABLE',
      };
    } catch (error) {
      console.error(`Error checking access for feature ${featureName}:`, error);
      return {
        errorCode: 'NETWORK_ERROR',
        message: 'Failed to check feature access',
        isSuccess: false,
      };
    }
  },

  // Create a subscription with Stripe
  async createSubscription(planId: string): Promise<CreateSubscriptionResponse> {
    const baseUrl = window.location.origin;
    const request = {
      planId,
      successUrl: `${baseUrl}/subscription?success=true`,
      cancelUrl: `${baseUrl}/subscription?canceled=true`,
    };
    try {
      return await apiClient.post<CreateSubscriptionResponse>(
        '/subscriptions/checkout',
        request
      );
    } catch (error) {
      console.error('Error creating subscription:', error);
      return {
        isSuccess: false,
        message: 'Failed to create subscription',
      };
    }
  },

  // Activate Pro subscription
  async activatePro(months: number = 1): Promise<FeatureAccessResponse> {
    const request: ActivateSubscriptionRequest = { months };
    try {
      return await apiClient.post<FeatureAccessResponse>(
        '/subscription/activate',
        request
      );
    } catch (error) {
      console.error('Error activating subscription:', error);
      return {
        errorCode: 'NETWORK_ERROR',
        message: 'Failed to activate subscription',
        isSuccess: false,
      };
    }
  },

  // Cancel Pro subscription
  async cancelPro(): Promise<FeatureAccessResponse> {
    try {
      return await apiClient.post<FeatureAccessResponse>('/subscriptions/cancel', {});
    } catch (error) {
      console.error('Error canceling subscription:', error);
      return {
        errorCode: 'NETWORK_ERROR',
        message: 'Failed to cancel subscription',
        isSuccess: false,
      };
    }
  },
};
